import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import U2000DetailTable from "../../../../../u2000Core/components/tables/detail/U2000DetailTable";
import { useU2000GridStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { type U2000TableColumn } from "../../../../../u2000Core/components/tables/basic/U2000BasicTableList";
import type { U3121ScopePeriod } from "../../../../U3121Service";
import { NS } from "../../../../U3121";
import type { U3121ProjectFormExtendedHandlers } from "../u3121ProjectContactsTab/U3121ProjectContactHandlers";

/** Ligne affichée : scopePeriod avec id synthétique pour la table. */
export interface U3121ScopePeriodRow extends U3121ScopePeriod {
  id: string;
}

function getColumnsDef(t: TFunction): U2000TableColumn[] {
  return [
    {
      name: "startDate",
      label: t("lblStartDate", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      isDefaultSortedColumn: true,
      widthClass: "w-[50%]",
      type: "string",
      align: "left",
    },
    {
      name: "endDate",
      label: t("lblEndDate", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[50%]",
      type: "string",
      align: "left",
    },
  ];
}

interface U3121ProjectScopePeriodBodyProps {
  formStateHandlers: U3121ProjectFormExtendedHandlers;
}

/**
 * Onglet Périodes de portée (projet) : tableau lecture seule des dates début/fin.
 * Données fournies par le GET projet (scopePeriods). Aucune action ajouter/modifier/supprimer.
 */
const U3121ProjectScopePeriodBody: React.FC<U3121ProjectScopePeriodBodyProps> = ({ formStateHandlers }) => {
  const { t, i18n } = useTranslation();
  const project = formStateHandlers.getData?.() ?? null;
  const scopePeriods = project?.scopePeriods ?? [];

  const scopePeriodsDataKey = useMemo(() => {
    const arr = Array.isArray(scopePeriods) ? scopePeriods : [];
    return `${project?.id ?? ""}_${arr.length}_${arr.map((r, i) => `${r.employee?.id ?? i}-${r.startDate}-${r.endDate}`).join(",")}`;
  }, [project?.id, scopePeriods]);

  const gridStateHandlers = useU2000GridStateHandlers<U3121ScopePeriodRow>();

  useEffect(() => {
    const columns = getColumnsDef(t);
    gridStateHandlers.setColumns(columns);
    gridStateHandlers.setSelectedFields(columns.filter((col) => col.isDefaultColumn).map((col) => col.name));
    gridStateHandlers.setSelectedSortedFields(columns.filter((col) => col.isDefaultSortedColumn).map((col) => ({ name: col.name, sortOrder: "ASC" as const })));
    const rawList = Array.isArray(scopePeriods) ? scopePeriods : [];
    const list: U3121ScopePeriodRow[] = rawList.map((r, idx) => ({
      ...r,
      id: r.employee?.id ? `${r.employee.id}-${r.startDate ?? ""}-${r.endDate ?? ""}-${idx}` : `scope-${idx}`,
    }));
    gridStateHandlers.setData(list);
    gridStateHandlers.setTotalRows(list.length);
    gridStateHandlers.setErrors([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- gridStateHandlers volontairement exclu des deps
  }, [scopePeriodsDataKey, t, i18n.language]);

  return (
    <U2000DetailTable
      gridStateHandlers={gridStateHandlers}
      allowRowSelection={false}
      label={t("lblScopePeriods", { count: gridStateHandlers.getTotalRows(), ns: NS })}
    />
  );
};

export default U3121ProjectScopePeriodBody;
